import { useEffect, useRef, useState } from "react";
import { atom, useAtom } from "jotai";
import { VerticalNavigation } from "side-ui";

import { queryClient } from "@App";
import { redirectIfAccessError } from "@lib/access/authRedirect";
import * as backend from "@lib/api";
import { checkGoogleAuthentication } from "@lib/api/checkGoogleAuthentication";
import { customerIoReset } from "@lib/customerio";
import { i18n } from "@lib/i18n";
import { queries } from "@lib/queries";
import changeLocale from "@lib/utils/changeLocale";
import { clearLogin } from "@lib/utils/clearLogin";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "@tanstack/react-router";

import { MenuMainItem } from "./MenuMainItem";

export const navigationModeAtom = atom("expanded");

const Navigation = () => {
    const location = useLocation();

    const navigate = useNavigate();

    const { data: user } = useQuery(queries.user.detail());
    // VerticalNavigation reads these fields without a loading check during scope changes.
    const lastKnownNavigationUser = useRef<{ firstName: string; lastName: string } | undefined>(
        undefined,
    );
    useEffect(() => {
        if (user) {
            lastKnownNavigationUser.current = {
                firstName: user.firstName,
                lastName: user.lastName,
            };
        }
    }, [user]);
    const navigationUser = user ?? lastKnownNavigationUser.current;
    const { data: attendancesToValidate } = useQuery(queries.attendance.toValidateCount());

    const [navigationMode, setNavigationMode] = useAtom(navigationModeAtom);
    const [active, setActive] = useState(location.pathname);
    const isShrinked = navigationMode === "shrinked";
    const [error, setError] = useState(null);

    type Children = {
        objectID: string;
        sublabel: string;
        label: string;
        pictureUrl: string;
    };

    const [children, setChildren] = useState<Children[]>([]);

    const normalizeChildren = (data: {
        Organisations: {
            id: string;
            address: string;
            name: string;
            pictureURL: string;
        }[];
    }) => {
        let normalizedChildren: Children[] = [];
        if (data?.Organisations.length > 0) {
            normalizedChildren = data.Organisations.map((child) => ({
                objectID: child.id,
                sublabel: child.address,
                label: child.name,
                pictureUrl: child.pictureURL,
            }));
        }
        return normalizedChildren;
    };

    async function getChildren(searchFieldValue) {
        let res;
        try {
            res = await backend.searchOrganisation({
                q: searchFieldValue,
            });
        } catch (e) {
            if (redirectIfAccessError(e, "query")) return;
            setError(e.toString());
        }
        if (res) {
            if (res.status >= 400) {
                setError(res.statusText);
                return;
            }

            const json = await res.json();
            const normalizedData = normalizeChildren(json);
            if (json) {
                setChildren(normalizedData);
            }
        }
    }

    const { data: company } = useQuery(queries.company.detail());
    const { data: featuresPolicy } = useQuery(queries.company.featuresPolicy());

    useEffect(() => {
        if (orgaIsCompany && orgaIsGroup) {
            getChildren("");
        }

        if (localStorage.getItem("google_login")) {
            checkGoogleAuthentication();
        }
    }, [company]);

    const orgaIsGroup = !!localStorage.getItem(`side_team_groupId`);
    const orgaIsCompany = !!localStorage.getItem(`side_team_activeOrganisationId`);
    const unsignedCountQuery = queries.documents.unsignedCount();
    const { data: toSignCount } = useQuery({
        ...unsignedCountQuery,
        enabled:
            unsignedCountQuery.enabled &&
            (orgaIsCompany || !!featuresPolicy?.networkLevelContractsPolicy.enabled),
    });
    const groupId = localStorage.getItem("side_team_groupId");
    const isLogas = Boolean(localStorage.getItem("side_team_logasId"));
    const isInsider = !!localStorage.getItem(`side_team_logas_email`);
    const networkLevelAttendancesPolicy = featuresPolicy?.networkLevelAttendancesPolicy;
    const canAccessNetworkAttendancesByLogasPolicy = networkLevelAttendancesPolicy?.logasOnly
        ? isLogas
        : true;

    const isEnglish = localStorage.getItem("side_team_locale") === "en";

    const goBackHome = () =>
        orgaIsGroup
            ? (localStorage.removeItem(`side_team_activeOrganisationId`),
              queryClient.invalidateQueries(queries.company.detail()),
              queryClient.invalidateQueries(queries.jobDescriptions.list()),
              navigate({ to: "/home" }))
            : navigate({ to: `/tasks` });

    // list of actions to display in the header
    const headerActions = [
        {
            icon: "House",
            label: i18n.header_backhome(),
            action: () => goBackHome(),
            show: () => orgaIsCompany === true && orgaIsGroup === true,
        },
    ].filter((item) => !item.show || item.show());

    // list of actions in the footer dropdown
    const footerActions = [
        {
            label: i18n.header_dropdown_personal_settings(),
            action: () => {
                navigate({ to: `/me/profile` });
            },
            icon: "Information",
            customClass: `--with_border-bottom`,
        },
        {
            label: i18n.header_dropdown_help(),
            action: () => window.open(`https://support.side.co/b2b/fr`),
            icon: "Book",
        },
        {
            label: i18n.header_dropdown_language(),
            action: () => changeLocale(isEnglish ? "fr" : "en"),
            icon: isEnglish ? "FlagFR" : "FlagUK",
        },
        {
            label: i18n.header_dropdown_logout(),
            action: () => {
                customerIoReset();
                queryClient.removeQueries();
                if (isInsider) {
                    localStorage.removeItem("side_team_activeOrganisationId");
                    window.location.assign("/side-admin");
                } else {
                    clearLogin();
                    window.location.assign("/signin");
                }
            },
            icon: "Power",
            customClass: `--color-red`,
        },
    ];

    const groupMobileItems = [
        {
            key: "/settings/team",
            label: i18n.header_team(),
            icon: "UserTeam",
            show: () => orgaIsGroup === true && !orgaIsCompany,
        },
    ]
        .filter((item) => !item.show || item.show())
        .map((item) => {
            return {
                ...item,
                action: () => {
                    setActive(item.key);
                    navigate({ to: item.key });
                },
                isActive: active.includes(item.key),
            };
        });

    const companyNavItems = [
        {
            key: "/home",
            label: i18n.header_search_company(),
            icon: "CompanySearch",
            show: () => orgaIsGroup === true && !orgaIsCompany,
        },
        {
            key: "/settings/team",
            label: i18n.header_team(),
            icon: "UserTeam",
            show: () => orgaIsGroup === true && !orgaIsCompany,
        },
        {
            key: "/planning",
            label: i18n.header_planning(),
            icon: "Calendar",
            show: () => orgaIsCompany === true,
        },
        {
            key: "/tasks",
            label: i18n.header_tasks(),
            icon: "Briefcase",
            show: () => orgaIsCompany === true,
        },
        {
            key: "/job-descriptions",
            label: i18n.header_job_descriptions(),
            icon: "Bolt",
            show: () => user?.role !== "collaborator",
        },
        {
            key: "/contracts",
            label: i18n.header_contracts(),
            icon: "DocumentLines",
            notifications: toSignCount,
            show: () =>
                (orgaIsCompany || featuresPolicy?.networkLevelContractsPolicy.enabled) &&
                user &&
                ["admin", "user"].includes(user?.role),
        },
        {
            key: "/network/attendances",
            label: i18n.header_timesheets(),
            icon: "ClockXL",
            show: () =>
                orgaIsGroup === true &&
                !orgaIsCompany &&
                networkLevelAttendancesPolicy?.enabled &&
                canAccessNetworkAttendancesByLogasPolicy,
        },
        {
            key: "/network/tasks",
            label: i18n.header_tasks_v2(),
            icon: "Briefcase",
            show: () => orgaIsGroup === true && !orgaIsCompany,
        },
        {
            key: "/timesheets",
            label: i18n.header_timesheets(),
            icon: "ClockXL",
            notifications: attendancesToValidate?.count,
            show: () => orgaIsCompany === true,
        },
        {
            key: "/invoices",
            label: i18n.header_invoices(),
            icon: "EuroSign",
            show: () => user && ["admin", "user"].includes(user?.role) && orgaIsCompany === true,
        },
        {
            key: "/settings",
            label: i18n.header_settings(),
            icon: "Cog",
            show: () => user && ["admin", "user"].includes(user?.role) && orgaIsCompany === true,
        },
    ]
        .filter((item) => !item.show || item.show())
        .map((item) => {
            return {
                ...item,
                action: () => {
                    setActive(item.key);
                    navigate({ to: item.key });
                },
                isActive: active.includes(item.key),
            };
        });

    const connectToChild = (option) => {
        localStorage.setItem(`side_team_activeOrganisationId`, option.objectID);

        if (location.pathname.includes("/taskPosting")) {
            navigate({
                reloadDocument: true,
                to: "/planning",
            });
        } else if (location.pathname.includes("/signature")) {
            navigate({
                reloadDocument: true,
                to: "/contracts",
            });
        } else if (location.pathname.includes("/job-descriptions")) {
            navigate({
                reloadDocument: true,
                to: "/job-descriptions",
            });
        } else {
            navigate({
                reloadDocument: true,
                to: location.pathname,
                search: location.search,
            });
        }
    };

    return (
        <div>
            {navigationUser && (
                <VerticalNavigation
                    headerDropdownId={groupId && `switch_${groupId}`}
                    headerDropdownOnChange={(value) => getChildren(value)}
                    headerDropdownOnOptionClick={(option) => connectToChild(option)}
                    headerDropdownSelectedOption={
                        orgaIsGroup &&
                        orgaIsCompany && {
                            objectID: localStorage.getItem(`side_team_activeOrganisationId`),
                            label: company?.organisation.name,
                        }
                    }
                    headerDropdownPlaceholder={i18n.header_switch_search_placeholder()}
                    headerDropdownError={error}
                    headerDropdownOptions={children}
                    headerLogoOnClick={() => goBackHome()}
                    headerActions={headerActions}
                    menuItems={companyNavItems}
                    menuMainItem={
                        user && ["admin", "user"].includes(user?.role) && orgaIsCompany === true ? (
                            <MenuMainItem isShrinked={isShrinked} />
                        ) : null
                    }
                    groupItems={groupMobileItems}
                    user={navigationUser}
                    footerActions={footerActions}
                    group={
                        localStorage.getItem(`side_team_groupId`) &&
                        company &&
                        company?.parent?.length > 0
                            ? { name: company?.parent, logoUrl: company?.organisation.logoUrl }
                            : null
                    }
                    entity={
                        company && company?.organisation?.name?.length > 0
                            ? company?.organisation
                            : null
                    }
                    isShrinked={isShrinked}
                    setIsShrinked={(bool) => {
                        setNavigationMode(bool ? "shrinked" : "expanded");
                    }}
                />
            )}
        </div>
    );
};

export default Navigation;
